import { RBAC_PERMISSION_EXPANSIONS } from "@/constants/rbac";
import { getStoredPermissions } from "@/lib/auth";

function normalizePermission(permission) {
  return String(permission || "").trim().toLowerCase();
}

/** True when the user has this single RBAC key. */
export function hasPermission(granted, key) {
  if (!key) return false;
  const grantedSet = new Set(
    (granted || [])
      .filter((p) => typeof p === "string")
      .map(normalizePermission),
  );
  if (!grantedSet.size) return false;
  return grantedSet.has(normalizePermission(key));
}

/** True when the user has at least one key from the list (tab / route visibility). */
export function hasAnyPermission(granted, keys) {
  if (!keys?.length) return false;
  return keys.some((key) => hasPermission(granted, key));
}

/** True when the user has every key in the list. */
export function hasAllPermissions(granted, keys) {
  if (!keys?.length) return false;
  return keys.every((key) => hasPermission(granted, key));
}

/** Expand legacy/parent keys into component-level keys (multi-pass for nested parents). */
export function expandPermissions(granted) {
  const expanded = new Set(
    (granted || [])
      .filter((p) => typeof p === "string")
      .map(normalizePermission),
  );

  let added = true;
  while (added) {
    added = false;
    for (const key of [...expanded]) {
      for (const [parent, children] of Object.entries(RBAC_PERMISSION_EXPANSIONS)) {
        if (normalizePermission(parent) !== key) continue;
        for (const child of children) {
          const normalizedChild = normalizePermission(child);
          if (!expanded.has(normalizedChild)) {
            expanded.add(normalizedChild);
            added = true;
          }
        }
      }
    }
  }

  return [...expanded];
}

/**
 * Effective RBAC keys for the signed-in user.
 * Source of truth: OTP verify response persisted in localStorage (`mu_permissions`).
 */
export function resolveEffectivePermissions(user) {
  const stored = getStoredPermissions();
  const fromUser = Array.isArray(user?.rbac) ? user.rbac : [];
  const merged = [...new Set([...stored, ...fromUser].filter(Boolean))];
  return expandPermissions(merged);
}
